import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Typography from '../../atoms/typography';
import { StyledContent } from '../../theme/page';
import Button from '../../atoms/button';

const SectionWrapper = styled.section`
  background-color: ${(props) => props.theme.color.brandNeutral};
  position: relative;
  z-index: ${({ theme }) => theme.layers.backstage};

  @media (min-width: ${(props) => props.theme.breakpoint.small}) {
    align-items: center;
    display: flex;
    min-height: ${(props) => (props.hasImage ? 'calc(35rem + 6rem)' : '150px')};
  }

  ::before,
  ::after {
    background-image: url(${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-header-bg.jpg);
    background-repeat: no-repeat;
    content: "";
    height: 100%;
    position: absolute;
    top: 0;
    width: 550px;
    z-index: -1;

    @media (min-width: ${(props) => props.theme.breakpoint.small}) {
      max-width: 50%;
    }
  }

  ::before {
    background-position: 0 0;
    display: none;
    left: 0;

    @media (min-width: ${(props) => props.theme.breakpoint.small}) {
      display: block;
    }
  }

  ::after {
    background-position: right 0;
    right: 0;
  }

  ${StyledContent} {
    width: 100%;

    @media (min-width: ${(props) => props.theme.breakpoint.small}) {
      align-items: center;
      display: flex;
      justify-content: center;
    }
  }
`;

const BrandContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  text-align: center;

  @media (min-width: ${(props) => props.theme.breakpoint.small}) {
    flex: 0 0 auto;
    width: 50%;
  }
`;

const Heading = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  line-height: 1.2;
`;

const SubHeading = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  letter-spacing: 0.24em;
  margin-bottom: ${(props) => props.theme.modularScale.medium};
  text-transform: uppercase;
`;

const Paragraph = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  line-height: 1.5;
  margin-top: 4rem;
  max-width: 400px;
`;

const BrandFigure = styled.figure`
  justify-content: center;

  @media (min-width: ${(props) => props.theme.breakpoint.small}) {
    flex: 0 0 auto;
    margin-left: 60px;
    width: 304px;
  }

  ${(props) => (props.mobile ? `
    margin-top: ${props.theme.modularScale.medium};

    @media (min-width: ${props.theme.breakpoint.small}) {
      display: none;
    }
  ` : `
    display: none;

    @media (min-width: ${props.theme.breakpoint.small}) {
      display: block;
    }
  `)}
`;

const BrandImage = styled.img`
  display: block;
  border-radius: 9999px;
  max-width: 100%;
`;

const FilterWrapper = styled.div`
  display: grid;
  grid-gap: 1.6rem;
  grid-template-columns: repeat(2, minmax(0, 12rem));
  margin-top: ${(props) => props.theme.modularScale.xlarge};

  @media (min-width: ${(props) => props.theme.breakpoint.small}) {
    grid-template-columns: repeat(2, minmax(0, 13rem));
    margin-top: ${(props) => props.theme.modularScale['2xlarge']};
  }

  @media (min-width: ${(props) => props.theme.breakpoint.large}) {
    grid-template-columns: repeat(4, minmax(0, 13rem));
  }
`;

const FilterLink = styled(Button)`
  display: flex;
  flex-basis: 100%;
  justify-content: center;
  line-height: 2.8rem; /* 32 - 4 (borders) */
  padding: 0;
`;

const Filters = (props) => {
  const router = useRouter();

  const getHref = (filters) => {
    const { query: { brand } } = router;
    let pathname = `/brands/${brand}`;

    const { cats = null, af = null } = filters;
    pathname = cats ? pathname += `/${cats.join('/')}` : pathname;
    pathname = af ? pathname += `?af=${af.join(' ')}` : pathname;

    return pathname;
  };

  const isFacetActive = (filters) => {
    const url = new URL(router.asPath, process.env.NEXT_PUBLIC_CLIENT_HOST);
    const facetUrl = new URL(getHref(filters), process.env.NEXT_PUBLIC_CLIENT_HOST);
    [...url.searchParams.keys()].forEach((key) => {
      if (key !== 'af') url.searchParams.delete(key);
    });
    return decodeURIComponent(facetUrl.href) !== decodeURIComponent(url.href);
  };

  if (!props.filters || props.filters.length === 0) return null;

  return (
    <FilterWrapper>
      {
        props.filters.map(([name, filters]) => (
          <Link key={name} href={getHref(filters)} passHref>
            <FilterLink
              outline={isFacetActive(filters)}
              text={name}
              isLink
            />
          </Link>
        ))
      }
    </FilterWrapper>
  );
};

Filters.defaultProps = {
  filters: null
};

Filters.propTypes = {
  filters: PropTypes.array
};

const PageHeader = (props) => (
  <SectionWrapper hasImage={props.image}>
    <StyledContent>
      <BrandContent>
        <header>
          {props.children}

          {
            props.subHeading
              ? <SubHeading element="h2" like="label-1">{props.subHeading}</SubHeading>
              : ''
          }

          {
            props.heading
              ? <Heading element="h1" like="heading-3">{props.heading}</Heading>
              : ''
          }
        </header>

        {
          props.image && (
            <BrandFigure mobile aria-hidden="true">
              <BrandImage src={props.image} alt={props.heading} />
            </BrandFigure>
          )
        }

        {
          props.text
            ? <Paragraph element="p" like="paragraph-2">{props.text}</Paragraph>
            : ''
        }

        <Filters filters={props.brandFilters} />
      </BrandContent>

      {
        props.image && (
          <BrandFigure>
            <BrandImage src={props.image} alt={props.heading} />
          </BrandFigure>
        )
      }
    </StyledContent>
  </SectionWrapper>
);

PageHeader.defaultProps = {
  brandFilters: null,
  children: null,
  heading: '',
  image: '',
  subHeading: '',
  text: ''
};

PageHeader.propTypes = {
  brandFilters: PropTypes.array,
  children: PropTypes.node,
  heading: PropTypes.node,
  image: PropTypes.string,
  subHeading: PropTypes.node,
  text: PropTypes.string
};

export default PageHeader;
